/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ChannelsService from '@/services/channels.service';
import Header from '@/shared-components/forms/add-board-content/add-post/inside-header/index';
import PostBodyComposer from '@/shared-components/forms/add-board-content/add-post/PostBodyComposer';
import Utils from '@/utils';

import "./index.css";

export default function AddPostForm({
  postData = null,
  editMode = false
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // 1️⃣ If we arrived via the “Save as draft” dialog, post-data is in location.state.postData
  const draftFromNav = location.state?.postData;
  //    Otherwise fall back to the prop (published-edit flow) or draft
  const effectivePostData = postData || draftFromNav;

  // 2️⃣ Build initial form state (seeds cover/desc/etc.), *and* preserve server’s post_status
  const initialFormState = useMemo(() => {
    const base = Utils.initPostFormState(effectivePostData);
    return {
      ...base,
      // if we’re editing an existing post, carry over its status;
      // otherwise fall back to whatever initPostFormState gave us
      post_status: effectivePostData?.post_status ?? base.post_status,
      publishedAt: effectivePostData?.publishedAt   ?? base.publishedAt,
    };
  }, [effectivePostData]);
  const [formState, setFormState] = useState(initialFormState);
  const [membersAudienceEnabled, setMembersAudienceEnabled] = useState(null);
  const [error, setError]       = useState(null);
  const editableRef             = useRef(null);
  const [isDraftDialogOpen, setIsDraftDialogOpen] = useState(false);

  useEffect(() => {
    if (membersAudienceEnabled === false && formState.audience === 'vip_only') {
      setFormState(prev => ({ ...prev, audience: 'public' }));
    }
  }, [membersAudienceEnabled, formState.audience]);

  useEffect(() => {
    let active = true;
    ChannelsService.getContentAudienceEligibility()
      .then(({ response, data }) => {
        if (!active) return;
        setMembersAudienceEnabled(Boolean(response?.ok && data?.membersAudienceEnabled));
      })
      .catch(() => {
        if (active) setMembersAudienceEnabled(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // ─── Hydrate saved blocks (texts/polls/pictures/etc.) on mount ───
  useEffect(() => {
    const c = effectivePostData?.post_container;
    // If there’s no container (brand‐new post), skip
    if (!c) return;

    // 2) Merge _all_ of it back into formState so BoardTextBody can render everything
    setFormState(prev => ({
      ...prev,
      post_structure: c.post_structure || [],
      post_texts: c.post_texts    || [],
      post_pictures: c.post_pictures || [],
      post_artworks: c.post_artworks || [],
      post_videos: c.post_videos || [],
      post_polls: (c.post_polls    || []).map(poll => ({ ...poll, published: true })),
      post_quizzes: (c.post_quizzes  || []).map(quiz => ({ ...quiz, published: true })),
    }));
  }, [effectivePostData]);

  return (
    <div className="add-posts__container">
      {/* Header (back, save-draft, post) */}
      <Header 
        formState={formState}
        setFormState={setFormState}
        formError={error}
        setFormError={setError}
        editMode={editMode}
        onDraftDialogToggle={setIsDraftDialogOpen}
        fetchData={effectivePostData}
        membersAudienceEnabled={membersAudienceEnabled}
      />

      {/* Body */}
      <main className="add-posts__main">
        <form onSubmit={e => e.preventDefault()} className="add-posts__main-form">
          <div className="add-posts__article-section">
            <PostBodyComposer
              formState={formState}
              setFormState={setFormState}
              editableRef={editableRef}
              hideAddButton={isDraftDialogOpen}
            />
          </div>
        </form>
      </main>
    </div>
  );
}
